// Authentication bridge utilities for cross-domain auth state sharing
// This handles setting and reading authentication cookies that can be shared between domains

interface AuthCookieData {
  isAuthenticated: boolean;
  token?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  currentPlan?: string | null;
  expiresAt: number;
}

const AUTH_COOKIE_NAME = 'cmi_auth_status';
const COOKIE_EXPIRY_HOURS = 24; // Cookie expires in 24 hours

/**
 * Sets authentication status in a cookie that can be shared across subdomains
 */
export const setAuthCookie = (authData: Omit<AuthCookieData, 'expiresAt'>) => {
  try {
    const expiresAt = Date.now() + (COOKIE_EXPIRY_HOURS * 60 * 60 * 1000);
    const cookieData: AuthCookieData = {
      ...authData,
      expiresAt
    };

    const cookieValue = btoa(JSON.stringify(cookieData)); // Base64 encode for safety
    const expiryDate = new Date(expiresAt).toUTCString();
    
    // Set cookie for the main domain (createmyinvoice.com) so it's accessible by subdomains
    document.cookie = `${AUTH_COOKIE_NAME}=${cookieValue}; expires=${expiryDate}; domain=.createmyinvoice.com; path=/; secure; samesite=lax`;
    
    console.log('Auth cookie set successfully');
  } catch (error) {
    console.error('Error setting auth cookie:', error);
  }
};

/**
 * Gets authentication status from the shared cookie
 */
export const getAuthCookie = (): AuthCookieData | null => {
  try {
    const cookies = document.cookie.split(';');
    const authCookie = cookies.find(cookie => 
      cookie.trim().startsWith(`${AUTH_COOKIE_NAME}=`)
    );

    if (!authCookie) {
      return null;
    }

    const cookieValue = authCookie.split('=')[1];
    const decodedData = atob(cookieValue); // Base64 decode
    const authData: AuthCookieData = JSON.parse(decodedData);

    // Check if cookie has expired
    if (Date.now() > authData.expiresAt) {
      clearAuthCookie();
      return null;
    }

    return authData;
  } catch (error) {
    console.error('Error reading auth cookie:', error);
    return null;
  }
};

/**
 * Clears the authentication cookie
 */
export const clearAuthCookie = () => {
  try {
    // Set cookie with past expiry date to delete it
    document.cookie = `${AUTH_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=.createmyinvoice.com; path=/;`;
    console.log('Auth cookie cleared');
  } catch (error) {
    console.error('Error clearing auth cookie:', error);
  }
};

/**
 * Updates auth cookie when Redux state changes
 */
export const syncAuthCookieWithRedux = (userState: any) => {
  if (userState.isAuthenticated) {
    setAuthCookie({
      isAuthenticated: true,
      token: userState.token,
      email: userState.email,
      firstName: userState.first_name,
      lastName: userState.last_name,
      currentPlan: userState.currentPlan
    });
  } else {
    clearAuthCookie();
  }
};

/**
 * Hook to check if user is authenticated (for use in Next.js)
 */
export const checkAuthStatus = (): {
  isAuthenticated: boolean;
  userInfo?: Omit<AuthCookieData, 'expiresAt'>;
} => {
  const authData = getAuthCookie();
  
  if (!authData || !authData.isAuthenticated) {
    return { isAuthenticated: false };
  }

  return {
    isAuthenticated: true,
    userInfo: {
      isAuthenticated: authData.isAuthenticated,
      token: authData.token,
      email: authData.email,
      firstName: authData.firstName,
      lastName: authData.lastName,
      currentPlan: authData.currentPlan
    }
  };
};
